import { useCallback, useEffect, useRef, useState } from 'react';
import { AllocationService, DateUtility, FeedService, ToastMessage } from '../utility';

const TERMINAL_FEED_STATUSES = new Set(['completed', 'failed']);
const POLL_MS = 4000;

export type UnassignedRefreshStatus = {
  canRefresh: boolean;
  blockReason: 'REFRESH_IN_PROGRESS' | 'REFRESH_COOLDOWN' | null;
  message: string | null;
  retryAfterSeconds?: number;
  activeFeed: {
    feedId: string;
    status: string;
    startedAt: string;
  } | null;
  lastCompletedAt: string | null;
  lastFailedAt: string | null;
};

export function useUnassignedAllocationRefresh(
  warehouseId: string | undefined,
  onComplete?: () => void,
) {
  const [statusLoading, setStatusLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<UnassignedRefreshStatus | null>(
    null,
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingFeedIdRef = useRef<string | null>(null);
  const postInFlightRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollingFeedIdRef.current = null;
  }, []);

  const loadRefreshStatus = useCallback(async () => {
    if (!warehouseId) return null;

    try {
      setStatusLoading(true);
      const response = await AllocationService.getUnassignedAllocationRefreshStatus({
        warehouseId,
      });
      setRefreshStatus(response);
      return response;
    } catch (error) {
      ToastMessage.apiError(error);
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [warehouseId]);

  const pollFeedUntilDone = useCallback(
    async (feedId: string) => {
      if (pollingFeedIdRef.current === feedId && pollRef.current) {
        return;
      }

      clearPoll();
      pollingFeedIdRef.current = feedId;
      setRefreshing(true);

      const checkFeed = async () => {
        try {
          const feed = await FeedService.getFeedStatus(feedId);

          if (!TERMINAL_FEED_STATUSES.has(feed.status)) {
            return;
          }

          clearPoll();
          setRefreshing(false);
          await loadRefreshStatus();

          if (feed.status === 'completed') {
            onComplete?.();
            return;
          }

          const errorMessage = feed.results?.errors?.[0]?.message || 'Auto assign failed';
          ToastMessage.apiError({ error: errorMessage });
        } catch (error) {
          clearPoll();
          setRefreshing(false);
          ToastMessage.apiError(error);
          loadRefreshStatus();
        }
      };

      await checkFeed();
      pollRef.current = setInterval(checkFeed, POLL_MS);
    },
    [clearPoll, loadRefreshStatus, onComplete],
  );

  const startRefresh = useCallback(async () => {
    if (!warehouseId || postInFlightRef.current) return;

    postInFlightRef.current = true;
    try {
      const response = await AllocationService.refreshUnassignedAllocations({
        warehouseId,
      });

      if (response.started === false) {
        if (response.reason === 'NO_UNASSIGNED_ALLOCATIONS') {
          ToastMessage.success('No unassigned allocations to auto assign');
        } else {
          ToastMessage.success('Nothing to auto assign');
        }
        await loadRefreshStatus();
        return;
      }

      if (response.feedId) {
        ToastMessage.success('Run auto assign started');
        await pollFeedUntilDone(response.feedId);
      }
    } catch (error) {
      const apiError = error as {
        status?: number;
        code?: string;
        error?: string;
        details?: { activeFeedId?: string; retryAfterSeconds?: number };
      };

      if (apiError.status === 409 && apiError.code === 'REFRESH_IN_PROGRESS') {
        ToastMessage.apiError({
          error:
            apiError.error || 'Auto assign is already in progress for this warehouse',
        });
        const status = await loadRefreshStatus();
        if (status?.activeFeed?.feedId) {
          await pollFeedUntilDone(status.activeFeed.feedId);
        }
        return;
      }

      if (apiError.status === 429 && apiError.code === 'REFRESH_COOLDOWN') {
        ToastMessage.apiError({
          error:
            apiError.error ||
            'Auto assign was run recently; try again after the cooldown',
        });
        await loadRefreshStatus();
        return;
      }

      setRefreshing(false);
      if (error.details?.retryAfterSeconds) {
        const cooldownPeriod = DateUtility.formatDurationFromSeconds(
          error.details.retryAfterSeconds,
        );
        ToastMessage.error(`Please try after cooldown period of ${cooldownPeriod}`);
      } else {
        ToastMessage.apiError(error);
      }
      loadRefreshStatus();
    } finally {
      postInFlightRef.current = false;
    }
  }, [warehouseId, pollFeedUntilDone, loadRefreshStatus]);

  useEffect(() => {
    loadRefreshStatus();
    return clearPoll;
  }, [loadRefreshStatus, clearPoll]);

  useEffect(() => {
    const activeFeed = refreshStatus?.activeFeed;
    if (
      !activeFeed?.feedId ||
      TERMINAL_FEED_STATUSES.has(activeFeed.status) ||
      pollingFeedIdRef.current
    ) {
      return;
    }

    pollFeedUntilDone(activeFeed.feedId);
  }, [
    refreshStatus?.activeFeed?.feedId,
    refreshStatus?.activeFeed?.status,
    pollFeedUntilDone,
  ]);

  const isProcessing = refreshing || refreshStatus?.blockReason === 'REFRESH_IN_PROGRESS';

  return {
    startRefresh,
    loadRefreshStatus,
    refreshing,
    isProcessing,
    statusLoading,
    refreshStatus,
  };
}
